import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, qs, type Paged } from '../api';
import { useAuth, canWrite } from '../auth';
import { AttentionStrip } from '../components/AttentionStrip';
import { BulkEdit } from '../components/BulkEdit';
import { ColorSwatch, ItemImage } from '../components/ItemImage';
import { ItemsViewSwitch } from '../components/ItemsViewSwitch';
import { LotFilters, pickFilters, type Filters } from '../components/LotFilters';
import { MarketFreshness, MonthsCell, SellThroughCell } from '../components/Market';
import type { MarketLotRow } from '../market';
import { Badge, Button, Card, Empty, ErrorMessage, PageHeader, Pagination, Select, Spinner, cn, money, number, unitMoney } from '../ui';

interface SavedSearch {
  id: number;
  name: string;
  query: Record<string, unknown>;
}

const STATUS_TONE = { draft: 'slate', live: 'green', stockroom: 'blue', sold_out: 'amber' } as const;
const STATUS_LABEL = { draft: 'Draft', live: 'Live', stockroom: 'Stockroom', sold_out: 'Sold out' } as const;

type SortKey = 'itemNo' | 'name' | 'qty' | 'incoming' | 'price' | 'updated' | 'sellThrough' | 'monthsOfStock';

export function Inventory() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showBulk, setShowBulk] = useState(false);

  const filters = useMemo(() => pickFilters(params), [params]);
  const page = Number(params.get('page') ?? 1) || 1;
  const sort = (params.get('sort') as SortKey) || 'itemNo';
  const dir = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const pageSize = 50;

  const lots = useQuery({
    queryKey: ['lots', filters, page, sort, dir],
    queryFn: () => api<Paged<MarketLotRow>>(`/api/v1/lots${qs({ ...filters, page, sort, dir, pageSize })}`),
    placeholderData: (prev) => prev,
  });

  const saved = useQuery({
    queryKey: ['saved-searches'],
    queryFn: () => api<{ savedSearches: SavedSearch[] }>('/api/v1/saved-searches').then((r) => r.savedSearches),
  });
  const navigate = useNavigate();
  // Stock that cannot be traced to a purchase goes in under "Opening stock", at no cost.
  const noSource = useMutation({
    mutationFn: () => api<{ batchId: number }>('/api/v1/opening-stock', { method: 'POST' }),
    onSuccess: (r) => navigate(`/batches/${r.batchId}/add`),
  });
  const saveSearch = useMutation({
    mutationFn: (name: string) => api('/api/v1/saved-searches', { body: { name, query: toQuery(filters) } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['saved-searches'] }),
  });

  // Changing a price or status here refreshes the list, the counts and the dashboard.
  const edit = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) => api(`/api/v1/lots/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lots'] });
      void qc.invalidateQueries({ queryKey: ['lots-attention'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
  const [rowError, setRowError] = useState<{ id: number; message: string } | null>(null);
  const saveRow = (id: number, body: Record<string, unknown>) => {
    setRowError(null);
    edit.mutate({ id, body }, { onError: (e) => setRowError({ id, message: e instanceof Error ? e.message : 'Could not save' }) });
  };

  const apply = (f: Filters) => {
    setSelected(new Set());
    setParams({ ...(f as Record<string, string>), view: 'lots' });
  };
  const setSort = (key: SortKey) => {
    const next = new URLSearchParams(params);
    next.set('sort', key);
    // Sell-through starts with the best sellers; every other column starts low to high.
    next.set('dir', sort === key ? (dir === 'asc' ? 'desc' : 'asc') : key === 'sellThrough' || key === 'incoming' ? 'desc' : 'asc');
    next.delete('page');
    setParams(next);
  };
  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    next.set('page', String(p));
    setParams(next);
  };

  const rows = lots.data?.rows ?? [];
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () =>
    setSelected((s) => {
      const n = new Set(s);
      for (const r of rows) allOnPage ? n.delete(r.id) : n.add(r.id);
      return n;
    });

  const exportLink = (format: string, mode: string) => `/api/v1/export/inventory${qs({ ...filters, format, mode })}`;

  return (
    <>
      <PageHeader
        title="Items"
        subtitle={lots.data ? `By lot: ${number(lots.data.total)} lot${lots.data.total === 1 ? '' : 's'} (an item in one colour and condition)` : undefined}
        actions={
          <>
          {user?.role !== 'picker' && <ItemsViewSwitch view="lots" />}
          {writable && <Button variant="secondary" disabled={noSource.isPending} onClick={() => noSource.mutate()} title="Stock you cannot trace to a purchase: it is added with no cost">
            {noSource.isPending ? 'Opening…' : 'Add stock with no purchase'}
          </Button>}
          <details className="relative">
            <summary className="inline-flex cursor-pointer list-none items-center rounded-md bg-white px-3.5 py-2 text-sm font-medium text-slate-800 ring-1 ring-slate-300 hover:bg-slate-50">
              Export
            </summary>
            <div className="absolute right-0 z-10 mt-1 w-64 space-y-1 rounded-md border border-slate-200 bg-white p-2 text-sm shadow-lg">
              <p className="px-2 pt-1 text-xs text-slate-500">Exports the current search.</p>
              {(
                [
                  ['csv', 'consolidated', 'CSV, one row per lot'],
                  ['csv', 'unconsolidated', 'CSV, one row per stock entry'],
                  ['json', 'consolidated', 'JSON'],
                  ['bsx', 'consolidated', 'BrickStore (BSX)'],
                  ['bricklink-xml', 'consolidated', 'BrickLink upload XML'],
                ] as const
              ).map(([format, mode, label]) => (
                <a key={label} href={exportLink(format, mode)} className="block rounded px-2 py-1.5 hover:bg-slate-100">
                  {label}
                </a>
              ))}
            </div>
          </details>
          </>
        }
      />

      <div className="space-y-4">
        <AttentionStrip active={filters.attention} onPick={(v) => apply({ ...filters, attention: v } as Filters)} />

        <Card
          actions={
            <>
              <Select
                aria-label="Saved searches"
                className="w-48"
                value=""
                onChange={(e) => {
                  const s = saved.data?.find((x) => String(x.id) === e.target.value);
                  if (s) apply(fromQuery(s.query));
                }}
              >
                <option value="">Saved searches…</option>
                {saved.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
              {writable && (
                <Button
                  variant="secondary"
                  small
                  disabled={Object.keys(filters).length === 0 || saveSearch.isPending}
                  onClick={() => {
                    const name = window.prompt('Name this search');
                    if (name?.trim()) saveSearch.mutate(name.trim());
                  }}
                >
                  Save search
                </Button>
              )}
            </>
          }
          title="Search"
        >
          <LotFilters value={filters} onApply={apply} />
          <div className="mt-2">
            <ErrorMessage error={saveSearch.error} />
          </div>
        </Card>

        {writable && lots.data && lots.data.total > 0 && (
          <Card
            title={
              selected.size > 0
                ? `${number(selected.size)} selected`
                : 'Bulk edit'
            }
            actions={
              <>
                {selected.size > 0 && (
                  <Button variant="ghost" small onClick={() => setSelected(new Set())}>
                    Clear selection
                  </Button>
                )}
                <Button variant="secondary" small onClick={() => setShowBulk((v) => !v)} aria-expanded={showBulk}>
                  {showBulk ? 'Hide' : 'Show'}
                </Button>
              </>
            }
          >
            {showBulk ? (
              <BulkEdit filters={filters} selectedIds={[...selected]} total={lots.data.total} />
            ) : (
              <p className="text-sm text-slate-500">Change prices, locks, locations or status for many lots at once, with a preview first.</p>
            )}
          </Card>
        )}

        <MarketFreshness />

        <ErrorMessage error={lots.error} />
        {lots.isPending ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty>
            {Object.keys(filters).length > 0 ? 'No lots match this search.' : 'No stock yet. Create a purchase, then add stock to a batch, or bring your BrickLink store in from Channels.'}
          </Empty>
        ) : (
          <div className={cn('overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm', lots.isFetching && 'opacity-70')}>
            <table className="data w-full min-w-[68rem] text-sm">
              <thead>
                <tr>
                  {writable && (
                    <th className="w-8">
                      <input type="checkbox" aria-label="Select all on this page" checked={allOnPage} onChange={toggleAll} />
                    </th>
                  )}
                  <th className="w-12" />
                  <SortTh label="Item" k="itemNo" sort={sort} dir={dir} onSort={setSort} />
                  <th>Colour</th>
                  <th>Cond.</th>
                  <SortTh label="Qty" k="qty" sort={sort} dir={dir} onSort={setSort} align="right" />
                  <SortTh label="Incoming" k="incoming" sort={sort} dir={dir} onSort={setSort} align="right" hint="Ordered but not yet received. Click to sort." />
                  <SortTh label="Price" k="price" sort={sort} dir={dir} onSort={setSort} align="right" />
                  <SortTh
                    label="Sell-through"
                    k="sellThrough"
                    sort={sort}
                    dir={dir}
                    onSort={setSort}
                    align="right"
                    hint="Of everything sold in the last 6 months or for sale now on BrickLink (all sellers), how much sold. Higher sells better."
                  />
                  <SortTh
                    label="Months of stock"
                    k="monthsOfStock"
                    sort={sort}
                    dir={dir}
                    onSort={setSort}
                    align="right"
                    hint="How many months everything now for sale on BrickLink (all sellers) would last at the pace of the last 6 months. Lower sells faster."
                  />
                  <th>Locations</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    {writable && (
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Select ${r.itemNo} ${r.colorName}`}
                          checked={selected.has(r.id)}
                          onChange={() =>
                            setSelected((s) => {
                              const n = new Set(s);
                              n.has(r.id) ? n.delete(r.id) : n.add(r.id);
                              return n;
                            })
                          }
                        />
                      </td>
                    )}
                    <td>
                      <ItemImage itemType={r.itemType} itemNo={r.itemNo} colorId={r.colorId} />
                    </td>
                    <td>
                      <Link to={`/lots/${r.id}`} className="font-medium text-slate-900 hover:underline">
                        {r.itemNo}
                      </Link>
                      <div className="max-w-xs truncate text-xs text-slate-500">{r.itemName}</div>
                    </td>
                    <td>
                      <ColorSwatch rgb={r.colorRgb} name={r.colorName} />
                    </td>
                    <td>
                      {r.condition === 'N' ? 'New' : 'Used'}
                      {r.subCondition && <span className="text-slate-400"> · {r.subCondition}</span>}
                    </td>
                    <td className="text-right tabular-nums">{number(r.qtyTotal)}{r.buildable > 0 && <div className="text-xs text-slate-500" title="More that could be built from loose parts">+{number(r.buildable)} can build</div>}</td>
                    <td className="text-right tabular-nums">{r.onOrder > 0 ? <Link className="text-sky-700 hover:underline" to={`/items/${r.itemId}?tab=incoming`}>{number(r.onOrder)}</Link> : <span className="text-slate-400">—</span>}</td>
                    <td className="text-right tabular-nums">
                      {writable && !r.priceLocked ? (
                        <input
                          key={`${r.id}-${r.price}`}
                          aria-label={`Price for ${r.itemNo}`}
                          defaultValue={r.price ?? ''}
                          inputMode="decimal"
                          placeholder="—"
                          className="w-20 rounded border border-transparent px-1 py-0.5 text-right tabular-nums hover:border-slate-300 focus:border-sky-500"
                          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                          onBlur={(e) => {
                            const v = e.currentTarget.value.trim();
                            if (v === (r.price ?? '') || (v !== '' && !Number.isFinite(Number(v)))) return;
                            if (v === '') return;
                            saveRow(r.id, { price: Number(v).toFixed(2) });
                          }}
                        />
                      ) : (
                        <>
                          {unitMoney(r.price)}
                          {r.priceLocked && (
                            <span title="Price locked" aria-label="Price locked" className="ml-1 text-slate-400">
                              🔒
                            </span>
                          )}
                        </>
                      )}
                      {rowError?.id === r.id && <div className="text-xs text-red-600">{rowError.message}</div>}
                    </td>
                    <td className="text-right">
                      <SellThroughCell market={r.market} />
                    </td>
                    <td className="text-right">
                      <MonthsCell market={r.market} />
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {r.locations.length === 0 ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          r.locations.map((l, i) => (
                            <span key={i} className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
                              {l.code ?? 'unplaced'}
                              <span className="text-slate-500">×{l.qty}</span>
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td>
                      {writable ? (
                        <Select aria-label={`Status for ${r.itemNo}`} className="w-28" value={r.status} onChange={(e) => saveRow(r.id, { status: e.target.value })}>
                          {(Object.keys(STATUS_LABEL) as (keyof typeof STATUS_LABEL)[]).map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABEL[s]}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {lots.data && lots.data.total > 0 && <Pagination page={page} pageSize={pageSize} total={lots.data.total} onPage={setPage} />}
      </div>
    </>
  );
}

function SortTh({ label, k, sort, dir, onSort, align, hint }: { label: string; k: SortKey; sort: SortKey; dir: string; onSort: (k: SortKey) => void; align?: 'right'; hint?: string }) {
  const active = sort === k;
  return (
    <th className={align === 'right' ? 'text-right' : undefined} aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => onSort(k)} title={hint} className="inline-flex items-center gap-1 uppercase hover:text-slate-900">
        {label}
        <span aria-hidden="true" className={active ? 'text-slate-900' : 'text-slate-300'}>
          {active && dir === 'desc' ? '↓' : '↑'}
        </span>
      </button>
    </th>
  );
}

/** URL strings → typed values for storing in a saved search. */
function toQuery(f: Filters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(f)) {
    if (!v) continue;
    out[k] = ['qtyMin', 'qtyMax', 'colorId', 'categoryId', 'priceMin', 'priceMax', 'sellThroughMin', 'sellThroughMax', 'monthsMin', 'monthsMax'].includes(k) ? Number(v) : ['priceLocked', 'includeEmpty'].includes(k) ? v === 'true' : v;
  }
  return out;
}

/** Saved-search values → URL strings. */
function fromQuery(q: Record<string, unknown>): Filters {
  return Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => [k, String(v)])) as Filters;
}

